import { type RouteRecordRaw } from 'vue-router';

import { usePermissionsStore } from '@/stores/permissions';

// Context shell
const SubcontractorContextContainer = () =>
  import('@/contexts/subcontractor/SubcontractorContextContainer.vue');
const SubcontractorContextNavigation = () =>
  import('@/contexts/subcontractor/SubcontractorContextNavigation.vue');
const SubcontractorSelectorCard = () =>
  import('@/contexts/subcontractor/SubcontractorSelectorCard.vue');

// Reused Depot view components — a subcontractor sees the same screens scoped
// to their subcontractor id (resolved from permissions), not the whole depot.
const SubcontractorLandingContainer = () =>
  import('@/views/subcontractor/SubcontractorLandingContainer.vue');
const LiveStatus = () => import('@/views/subcontractor/LiveStatus.vue');
const LiveStatusIssues = () =>
  import('@/contexts/subcontractor/liveStatus/LiveStatusIssues.vue');
const liveStatusDefaultChild = () => {
  const { subcontractorFeatures } = usePermissionsStore();

  return subcontractorFeatures.viewLiveIssues &&
    !subcontractorFeatures.viewLiveStatus
    ? { name: 'subcontractorLiveStatusIssues' }
    : { name: 'subcontractorLiveStatusTours' };
};
const UsageAnalysisContainer = () =>
  import('@/views/subcontractor/UsageAnalysisContainer.vue');
const TourPerformance = () =>
  import('@/views/subcontractor/TourPerformance.vue');
const TourPerformanceDetails = () =>
  import('@/views/subcontractor/TourPerformanceDetails.vue');
const TourDetailsViewContainer = () =>
  import('@/contexts/depot/liveStatus/LiveStatusTourDetails.vue');
const TourDrivenHistoryV2 = () =>
  import('@/contexts/depot/tourPerformance/drivenHistoryV2/TourDrivenHistory.vue');
const DriversContainer = () =>
  import('@/contexts/depot/drivers/DriversContainer.vue');

const subcontractorChildRoutes: RouteRecordRaw[] = [
  {
    component: SubcontractorLandingContainer,
    name: 'subcontractorLanding',
    path: 'depot-landing',
    meta: { titleKey: 'mainNavigation.depotLanding' }
  },
  {
    path: 'statistics/live-status',
    name: 'subcontractorLiveStatus',
    redirect: liveStatusDefaultChild,
    children: [
      {
        component: LiveStatus,
        name: 'subcontractorLiveStatusTours',
        path: 'tours',
        meta: { titleKey: 'mainNavigation.liveStatus' }
      },
      {
        component: TourDetailsViewContainer,
        name: 'subcontractorTourView',
        path: 'tours/:tourId',
        props: true,
        meta: {
          titleKey: 'mainNavigation.liveStatusTours',
          remountOnParamsChange: true
        }
      },
      {
        component: LiveStatusIssues,
        name: 'subcontractorLiveStatusIssues',
        path: 'issues',
        meta: { titleKey: 'mainNavigation.liveStatusIssues' }
      }
    ]
  },
  {
    component: UsageAnalysisContainer,
    name: 'subcontractorDailyActivities',
    path: 'statistics/activities/:date?',
    props: true,
    meta: { titleKey: 'mainNavigation.usageAnalysation' }
  },
  {
    component: TourPerformance,
    name: 'subcontractorTourPerformance',
    path: 'tour-performance',
    meta: { titleKey: 'mainNavigation.tourPerformance' }
  },
  {
    component: TourPerformanceDetails,
    name: 'subcontractorTourPerformanceDetails',
    path: 'tour-performance/details/:tour/:dates',
    props: true,
    meta: {
      titleKey: 'mainNavigation.tourPerformance',
      remountOnParamsChange: true
    }
  },
  {
    component: TourDrivenHistoryV2,
    name: 'subcontractorTourDrivenHistoryV2',
    path: 'tour-performance/history/v2/:tourId',
    props: true,
    meta: { titleKey: 'mainNavigation.tourPerformance' }
  },
  {
    component: DriversContainer,
    name: 'subcontractorAppAccounts',
    path: 'app-accounts',
    meta: { titleKey: 'mainNavigation.secondScreenAccounts' }
  }
];

export const subcontractorRoute: RouteRecordRaw = {
  path: '/subcontractor/:tenantId?/:externalDepotId?',
  components: {
    default: SubcontractorContextContainer,
    LeftSidebar: SubcontractorContextNavigation
  },
  props: true,
  children: [
    {
      path: '',
      name: 'subcontractor',
      component: SubcontractorSelectorCard,
      meta: {
        fillHeight: true,
        hasBasicLayout: true
      }
    },
    ...subcontractorChildRoutes
  ]
};
